import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './ContentFlow.module.css';
import { SitePage, SitePageType } from '../../Model/siteMap';
import { resumeExperienceRowTransitionIds } from '../../Model/resume';
import { linkRowTransitionIds } from '../../Model/links';
import ContentTile from './ContentTile';
import MenuItem from './MenuItem';
import MenuHeader from './MenuHeader';
import ContentOverview from './ContentOverview';
import ContentDetails, { CONTENT_DETAIL_TRANSITION_ID } from './ContentDetails';
import ResumeView from './ResumeView';
import LinksView from './LinksView';
import ParticleBackgroundCanvas from './webglBackground/ParticleBackgroundCanvas';
import {
    buildListSearch,
    filterPagesByType,
    parseContentLocation,
    readRouteFromWindow,
} from './contentFlowRouteUtils';
import { openContactEmail } from '../../Model/contactEmail';

const OVERVIEW_TRANSITION_ID = 'overview';
const MOBILE_MENU_ID = 'site-mobile-menu';
/** Overlay menu rows for coordinated paint-out (overlay title stays static). */
const MOBILE_MENU_PAINT_OUT_PARTS = 7;
const MOBILE_MENU_ROW_STAGGER_MS = 85;

function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);
    return isMobile;
}

type PendingTransition =
    | { kind: 'filter'; filter: SitePageType | null }
    | { kind: 'openDetail'; page: SitePage; listFilter: SitePageType | null }
    | { kind: 'mobileMenu' }
    | { kind: 'resume' }
    | { kind: 'links' };

/** How overlay rows animate closed (mobile). */
type MobileMenuPaintOutMode =
    | 'uniformWipe'
    | 'allFade'
    | { kind: 'selectedWipe'; row: number };

function mobileMenuPaintOutVariantForRow(
    mode: MobileMenuPaintOutMode,
    row: number,
): 'wipe' | 'fade' | 'wipeOverText' {
    if (mode === 'uniformWipe') return 'wipe';
    if (mode === 'allFade') return 'fade';
    return mode.row === row ? 'wipeOverText' : 'fade';
}

/** Stagger row paint-outs except selected-tap close, where fades stay in sync with the chosen row. */
function mobileOverlayPaintOutExitDelayMs(mode: MobileMenuPaintOutMode, row: number): number {
    if (typeof mode === 'object' && mode.kind === 'selectedWipe') {
        return 0;
    }
    return row * MOBILE_MENU_ROW_STAGGER_MS;
}

interface ContentFlowProps {}

const ContentFlow: React.FC<ContentFlowProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const bootRef = useRef<ReturnType<typeof readRouteFromWindow> | null>(null);
    if (!bootRef.current) {
        bootRef.current = readRouteFromWindow();
    }

    const [activeFilter, setActiveFilter] = useState(() => bootRef.current!.filter);
    const [selectedPage, setSelectedPage] = useState(() => bootRef.current!.page);
    const [renderedPages, setRenderedPages] = useState(() => bootRef.current!.pages);
    const [resumeMode, setResumeMode] = useState(() => bootRef.current!.showResume);
    const [linksMode, setLinksMode] = useState(() => bootRef.current!.showLinks);

    const [tilePhase, setTilePhase] = useState<'in' | 'out'>('in');
    const [renderCycle, setRenderCycle] = useState(0);
    const [transitionToken, setTransitionToken] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileOpenMenuIconExpanded, setMobileOpenMenuIconExpanded] = useState(false);
    const [mobileMenuPaintOutMode, setMobileMenuPaintOutMode] = useState<MobileMenuPaintOutMode>('uniformWipe');

    const pendingTransitionRef = useRef<PendingTransition | null>(null);
    const awaitingHiddenIdsRef = useRef<Set<string>>(new Set());
    const activeTokenRef = useRef(0);

    const resetScrollPosition = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, []);

    const completeTransition = useCallback(() => {
        const pending = pendingTransitionRef.current;
        pendingTransitionRef.current = null;

        resetScrollPosition();

        if (pending?.kind === 'mobileMenu') {
            setMobileMenuOpen(true);
            setMobileMenuPaintOutMode('uniformWipe');
            setIsTransitioning(false);
            return;
        }

        if (pending?.kind === 'openDetail') {
            setResumeMode(false);
            setLinksMode(false);
            setActiveFilter(pending.listFilter);
            setRenderedPages(filterPagesByType(pending.listFilter));
            setSelectedPage(pending.page);
            setRenderCycle((value) => value + 1);
            setTilePhase('in');
            setIsTransitioning(false);
            queueMicrotask(() => {
                navigate({
                    pathname: `/${pending.page.id}`,
                    ...(pending.listFilter ? { search: buildListSearch(pending.listFilter) } : {}),
                });
            });
            return;
        }

        if (pending?.kind === 'resume') {
            setLinksMode(false);
            setResumeMode(true);
            setActiveFilter(null);
            setRenderedPages([]);
            setSelectedPage(null);
            setRenderCycle((value) => value + 1);
            setTilePhase('in');
            setIsTransitioning(false);
            queueMicrotask(() => {
                navigate({ pathname: '/resume' });
            });
            return;
        }

        if (pending?.kind === 'links') {
            setResumeMode(false);
            setLinksMode(true);
            setActiveFilter(null);
            setRenderedPages([]);
            setSelectedPage(null);
            setRenderCycle((value) => value + 1);
            setTilePhase('in');
            setIsTransitioning(false);
            queueMicrotask(() => {
                navigate({ pathname: '/links' });
            });
            return;
        }

        if (pending?.kind === 'filter') {
            setResumeMode(false);
            setLinksMode(false);
            setActiveFilter(pending.filter);
            setRenderedPages(filterPagesByType(pending.filter));
            setSelectedPage(null);
            setRenderCycle((value) => value + 1);
            setTilePhase('in');
            setIsTransitioning(false);
            queueMicrotask(() => {
                navigate({
                    pathname: '/',
                    ...(pending.filter ? { search: buildListSearch(pending.filter) } : {}),
                });
            });
            return;
        }

        setIsTransitioning(false);
    }, [navigate, resetScrollPosition]);

    const beginTransition = useCallback(
        (pending: PendingTransition, awaitingIds: Set<string>) => {
            pendingTransitionRef.current = pending;
            awaitingHiddenIdsRef.current = awaitingIds;

            const token = activeTokenRef.current + 1;
            activeTokenRef.current = token;
            setTransitionToken(token);
            setTilePhase('out');
            setIsTransitioning(true);

            if (awaitingHiddenIdsRef.current.size === 0) {
                completeTransition();
            }
        },
        [completeTransition],
    );

    const startFilterTransition = useCallback(
        (nextFilter: SitePageType | null) => {
            if (isTransitioning) return;
            if (!selectedPage && nextFilter === activeFilter && !resumeMode && !linksMode) return;

            const awaitingIds = new Set<string>();
            if (selectedPage) {
                awaitingIds.add(CONTENT_DETAIL_TRANSITION_ID);
            } else if (resumeMode) {
                resumeExperienceRowTransitionIds().forEach((id) => awaitingIds.add(id));
            } else if (linksMode) {
                linkRowTransitionIds().forEach((id) => awaitingIds.add(id));
            } else {
                renderedPages.forEach((page) => awaitingIds.add(page.id));
                if (activeFilter === null) {
                    awaitingIds.add(OVERVIEW_TRANSITION_ID);
                }
            }

            beginTransition({ kind: 'filter', filter: nextFilter }, awaitingIds);
        },
        [activeFilter, beginTransition, isTransitioning, linksMode, renderedPages, resumeMode, selectedPage],
    );

    const startOpenDetailTransition = useCallback(
        (page: SitePage, opts?: { listFilter?: SitePageType | null }) => {
            if (isTransitioning) return;
            const listFilter = opts?.listFilter !== undefined ? opts.listFilter : activeFilter;
            if (selectedPage?.id === page.id && activeFilter === listFilter) return;

            const awaitingIds = new Set<string>();
            if (selectedPage) {
                awaitingIds.add(CONTENT_DETAIL_TRANSITION_ID);
            } else if (resumeMode) {
                resumeExperienceRowTransitionIds().forEach((id) => awaitingIds.add(id));
            } else if (linksMode) {
                linkRowTransitionIds().forEach((id) => awaitingIds.add(id));
            } else {
                const gridIds = new Set(renderedPages.map((p) => p.id));
                gridIds.forEach((id) => awaitingIds.add(id));
                if (activeFilter === null) {
                    awaitingIds.add(OVERVIEW_TRANSITION_ID);
                }
            }

            beginTransition({ kind: 'openDetail', page, listFilter }, awaitingIds);
        },
        [activeFilter, beginTransition, isTransitioning, linksMode, renderedPages, resumeMode, selectedPage],
    );

    const startResumeTransition = useCallback(() => {
        if (isTransitioning) return;
        if (!selectedPage && resumeMode) return;

        const awaitingIds = new Set<string>();
        if (selectedPage) {
            awaitingIds.add(CONTENT_DETAIL_TRANSITION_ID);
        } else if (linksMode) {
            linkRowTransitionIds().forEach((id) => awaitingIds.add(id));
        } else {
            renderedPages.forEach((page) => awaitingIds.add(page.id));
            if (activeFilter === null) {
                awaitingIds.add(OVERVIEW_TRANSITION_ID);
            }
        }

        beginTransition({ kind: 'resume' }, awaitingIds);
    }, [activeFilter, beginTransition, isTransitioning, linksMode, renderedPages, resumeMode, selectedPage]);

    const startLinksTransition = useCallback(() => {
        if (isTransitioning) return;
        if (!selectedPage && linksMode) return;

        const awaitingIds = new Set<string>();
        if (selectedPage) {
            awaitingIds.add(CONTENT_DETAIL_TRANSITION_ID);
        } else if (resumeMode) {
            resumeExperienceRowTransitionIds().forEach((id) => awaitingIds.add(id));
        } else {
            renderedPages.forEach((page) => awaitingIds.add(page.id));
            if (activeFilter === null) {
                awaitingIds.add(OVERVIEW_TRANSITION_ID);
            }
        }

        beginTransition({ kind: 'links' }, awaitingIds);
    }, [activeFilter, beginTransition, isTransitioning, linksMode, renderedPages, resumeMode, selectedPage]);

    const handleHideComplete = useCallback(
        (id: string, token: number) => {
            if (token !== activeTokenRef.current) return;
            if (!awaitingHiddenIdsRef.current.has(id)) return;

            awaitingHiddenIdsRef.current.delete(id);

            if (awaitingHiddenIdsRef.current.size === 0) {
                completeTransition();
            }
        },
        [completeTransition],
    );

    const isMobile = useIsMobile();

    const [menuPaintOutTick, setMenuPaintOutTick] = useState(0);
    const [menuPaintOutGen, setMenuPaintOutGen] = useState(0);
    const menuPaintOutGenRef = useRef(0);
    const mobileMenuOutCountRef = useRef(0);
    const mobileMenuOutThenRef = useRef<(() => void) | null>(null);

    const applyMobileNavFilterAfterMenu = useCallback(
        (nextFilter: SitePageType | null) => {
            const token = activeTokenRef.current + 1;
            activeTokenRef.current = token;
            setTransitionToken(token);
            setResumeMode(false);
            setLinksMode(false);
            setActiveFilter(nextFilter);
            setRenderedPages(filterPagesByType(nextFilter));
            setSelectedPage(null);
            setRenderCycle((value) => value + 1);
            setTilePhase('in');
            setIsTransitioning(false);
            queueMicrotask(() => {
                navigate({
                    pathname: '/',
                    ...(nextFilter ? { search: buildListSearch(nextFilter) } : {}),
                });
            });
        },
        [navigate],
    );

    const applyMobileResumeAfterMenu = useCallback(() => {
        const token = activeTokenRef.current + 1;
        activeTokenRef.current = token;
        setTransitionToken(token);
        setResumeMode(true);
        setLinksMode(false);
        setActiveFilter(null);
        setRenderedPages([]);
        setSelectedPage(null);
        setRenderCycle((value) => value + 1);
        setTilePhase('in');
        setIsTransitioning(false);
        queueMicrotask(() => {
            navigate({ pathname: '/resume' });
        });
    }, [navigate]);

    const applyMobileLinksAfterMenu = useCallback(() => {
        const token = activeTokenRef.current + 1;
        activeTokenRef.current = token;
        setTransitionToken(token);
        setResumeMode(false);
        setLinksMode(true);
        setActiveFilter(null);
        setRenderedPages([]);
        setSelectedPage(null);
        setRenderCycle((value) => value + 1);
        setTilePhase('in');
        setIsTransitioning(false);
        queueMicrotask(() => {
            navigate({ pathname: '/links' });
        });
    }, [navigate]);

    const revealContentAfterMenuClose = useCallback(() => {
        const token = activeTokenRef.current + 1;
        activeTokenRef.current = token;
        setTransitionToken(token);
        setTilePhase('in');
    }, []);

    const beginMobileMenuOpenTransition = useCallback(() => {
        if (isTransitioning) return;
        const awaitingIds = new Set<string>();
        if (selectedPage) {
            awaitingIds.add(CONTENT_DETAIL_TRANSITION_ID);
        } else if (resumeMode) {
            resumeExperienceRowTransitionIds().forEach((id) => awaitingIds.add(id));
        } else if (linksMode) {
            linkRowTransitionIds().forEach((id) => awaitingIds.add(id));
        } else {
            renderedPages.forEach((page) => awaitingIds.add(page.id));
            if (activeFilter === null) {
                awaitingIds.add(OVERVIEW_TRANSITION_ID);
            }
        }
        beginTransition({ kind: 'mobileMenu' }, awaitingIds);
    }, [activeFilter, beginTransition, isTransitioning, linksMode, renderedPages, resumeMode, selectedPage]);

    const scheduleMobileMenuPaintOut = useCallback(
        (then: () => void, mode: MobileMenuPaintOutMode = 'uniformWipe') => {
            mobileMenuOutCountRef.current = 0;
            menuPaintOutGenRef.current += 1;
            const nextGen = menuPaintOutGenRef.current;
            mobileMenuOutThenRef.current = then;
            setMobileMenuPaintOutMode(mode);
            setMenuPaintOutGen(nextGen);
            setMenuPaintOutTick((value) => value + 1);
        },
        [],
    );

    const onMobileMenuPaintOutPartDone = useCallback((generation: number) => {
        if (generation !== menuPaintOutGenRef.current) return;
        mobileMenuOutCountRef.current += 1;
        if (mobileMenuOutCountRef.current >= MOBILE_MENU_PAINT_OUT_PARTS) {
            const fn = mobileMenuOutThenRef.current;
            mobileMenuOutThenRef.current = null;
            fn?.();
        }
    }, []);

    const handleMobileMenuNavigate = useCallback(
        (nextFilter: SitePageType | null, opts?: { selectedRow?: number }) => {
            if (!selectedPage && nextFilter === activeFilter && !resumeMode && !linksMode) {
                scheduleMobileMenuPaintOut(() => {
                    setMobileMenuOpen(false);
                    revealContentAfterMenuClose();
                }, 'uniformWipe');
                return;
            }
            const mode: MobileMenuPaintOutMode =
                opts?.selectedRow !== undefined
                    ? { kind: 'selectedWipe', row: opts.selectedRow }
                    : 'allFade';
            scheduleMobileMenuPaintOut(() => {
                setMobileMenuOpen(false);
                applyMobileNavFilterAfterMenu(nextFilter);
            }, mode);
        },
        [
            activeFilter,
            applyMobileNavFilterAfterMenu,
            revealContentAfterMenuClose,
            scheduleMobileMenuPaintOut,
            resumeMode,
            linksMode,
            selectedPage,
        ],
    );

    const handleMobileResumeNavigate = useCallback(() => {
        if (!selectedPage && resumeMode) {
            scheduleMobileMenuPaintOut(() => {
                setMobileMenuOpen(false);
                revealContentAfterMenuClose();
            }, 'uniformWipe');
            return;
        }
        scheduleMobileMenuPaintOut(() => {
            setMobileMenuOpen(false);
            applyMobileResumeAfterMenu();
        }, { kind: 'selectedWipe', row: 4 });
    }, [
        applyMobileResumeAfterMenu,
        resumeMode,
        revealContentAfterMenuClose,
        scheduleMobileMenuPaintOut,
        selectedPage,
    ]);

    const handleMobileLinksNavigate = useCallback(() => {
        if (!selectedPage && linksMode) {
            scheduleMobileMenuPaintOut(() => {
                setMobileMenuOpen(false);
                revealContentAfterMenuClose();
            }, 'uniformWipe');
            return;
        }
        scheduleMobileMenuPaintOut(() => {
            setMobileMenuOpen(false);
            applyMobileLinksAfterMenu();
        }, { kind: 'selectedWipe', row: 5 });
    }, [
        applyMobileLinksAfterMenu,
        linksMode,
        revealContentAfterMenuClose,
        scheduleMobileMenuPaintOut,
        selectedPage,
    ]);

    const handleMobileContactNavigate = useCallback(() => {
        scheduleMobileMenuPaintOut(() => {
            setMobileMenuOpen(false);
            revealContentAfterMenuClose();
            queueMicrotask(() => openContactEmail());
        }, { kind: 'selectedWipe', row: 6 });
    }, [revealContentAfterMenuClose, scheduleMobileMenuPaintOut]);

    useLayoutEffect(() => {
        const parsed = parseContentLocation(location.pathname, location.search);
        if (parsed.unknownPageId) {
            navigate(
                {
                    pathname: '/',
                    ...(parsed.filter ? { search: buildListSearch(parsed.filter) } : {}),
                },
                { replace: true },
            );
        }
    }, [location.pathname, location.search, navigate]);

    useEffect(() => {
        setMobileMenuOpen(false);
        setMobileOpenMenuIconExpanded(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (mobileMenuOpen) {
            setMobileOpenMenuIconExpanded(false);
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            if (isMobile) {
                scheduleMobileMenuPaintOut(() => {
                    setMobileMenuOpen(false);
                    revealContentAfterMenuClose();
                }, 'uniformWipe');
            } else {
                setMobileMenuOpen(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isMobile, mobileMenuOpen, revealContentAfterMenuClose, scheduleMobileMenuPaintOut]);

    useEffect(() => {
        if (isTransitioning) return;

        const parsed = parseContentLocation(location.pathname, location.search);
        if (parsed.unknownPageId) return;

        const wantPage = parsed.page;
        const wantFilter = parsed.filter;
        const wantResume = parsed.showResume;
        const wantLinks = parsed.showLinks;

        if (wantPage) {
            if (selectedPage?.id === wantPage.id && activeFilter === wantFilter) {
                return;
            }
            if (selectedPage?.id === wantPage.id && activeFilter !== wantFilter) {
                setActiveFilter(wantFilter);
                setRenderedPages(filterPagesByType(wantFilter));
                return;
            }
            startOpenDetailTransition(wantPage, { listFilter: wantFilter });
            return;
        }

        if (wantResume) {
            if (resumeMode && !selectedPage) {
                return;
            }
            startResumeTransition();
            return;
        }

        if (wantLinks) {
            if (linksMode && !selectedPage) {
                return;
            }
            startLinksTransition();
            return;
        }

        if ((resumeMode || linksMode) && !wantPage) {
            startFilterTransition(wantFilter);
            return;
        }

        if (!selectedPage && activeFilter === wantFilter) {
            return;
        }

        startFilterTransition(wantFilter);
    }, [
        activeFilter,
        isTransitioning,
        linksMode,
        location.pathname,
        location.search,
        resumeMode,
        selectedPage,
        startFilterTransition,
        startLinksTransition,
        startOpenDetailTransition,
        startResumeTransition,
    ]);

    const contentViews = renderedPages.map((page) => {
        return (
            <ContentTile
                key={`${page.id}-${renderCycle}`}
                page={page}
                phase={tilePhase}
                transitionToken={transitionToken}
                onHideComplete={handleHideComplete}
                onOpenDetail={startOpenDetailTransition}
            />
        );
    });

    const desktopMenuLinks = (
        <>
            <MenuItem
                index={0}
                title="Selected Work"
                isSelected={activeFilter === SitePageType.Project}
                onClick={() => startFilterTransition(SitePageType.Project)}
            />
            <MenuItem
                index={1}
                title="iOS Apps"
                isSelected={activeFilter === SitePageType.iOSApp}
                onClick={() => startFilterTransition(SitePageType.iOSApp)}
            />
            <MenuItem
                index={2}
                title="Experiments"
                isSelected={activeFilter === SitePageType.Experiment}
                onClick={() => startFilterTransition(SitePageType.Experiment)}
            />
            <MenuItem
                index={3}
                title="3D Modeling/Printing"
                isSelected={activeFilter === SitePageType.ModelingPrinting}
                onClick={() => startFilterTransition(SitePageType.ModelingPrinting)}
            />
            <MenuItem
                index={4}
                title="Resume"
                isSelected={resumeMode}
                onClick={() => startResumeTransition()}
            />
            <MenuItem
                index={5}
                title="Links"
                isSelected={linksMode}
                onClick={() => startLinksTransition()}
            />
            <MenuItem index={6} title="Contact" isSelected={false} onClick={() => openContactEmail()} />
        </>
    );

    return (
        <>
            <ParticleBackgroundCanvas />
            <div className={`${styles.container}${mobileMenuOpen ? ` ${styles.containerMenuOpen}` : ''}`}>
            <header className={styles.mobileTopBar}>
                <div className={styles.mobileTopBarBrand}>
                    <MenuHeader
                        name="Jason K. Smith"
                        subtitle="Design Engineer"
                        animated={false}
                        disableHover
                        onClick={() => {
                            startFilterTransition(null);
                        }}
                    />
                </div>
                <button
                    type="button"
                    className={styles.mobileMenuButton}
                    aria-label="Open menu"
                    aria-expanded={mobileMenuOpen || mobileOpenMenuIconExpanded}
                    aria-controls={mobileMenuOpen ? MOBILE_MENU_ID : undefined}
                    onClick={() => {
                        if (isTransitioning) return;
                        if (isMobile) {
                            setMobileOpenMenuIconExpanded(true);
                            beginMobileMenuOpenTransition();
                        } else {
                            setMobileMenuOpen(true);
                        }
                    }}
                >
                    <span
                        className={`${styles.mobileMenuIcon}${
                            mobileMenuOpen || mobileOpenMenuIconExpanded ? ` ${styles.mobileMenuIconExpanded}` : ''
                        }`}
                        aria-hidden
                    >
                        <span className={styles.mobileMenuIconBar} />
                        <span className={styles.mobileMenuIconBar} />
                        <span className={styles.mobileMenuIconBar} />
                    </span>
                </button>
            </header>
            <div className={styles.content}>
                {activeFilter === null && !selectedPage && !resumeMode && !linksMode && (
                    <ContentOverview
                        phase={tilePhase}
                        transitionToken={transitionToken}
                        onHideComplete={handleHideComplete}
                    />
                )}
                {resumeMode && !selectedPage ? (
                    <ResumeView
                        key={`resume-${renderCycle}`}
                        phase={tilePhase}
                        transitionToken={transitionToken}
                        onHideComplete={handleHideComplete}
                    />
                ) : null}
                {linksMode && !selectedPage ? (
                    <LinksView
                        key={`links-${renderCycle}`}
                        phase={tilePhase}
                        transitionToken={transitionToken}
                        onHideComplete={handleHideComplete}
                    />
                ) : null}
                {selectedPage ? (
                    <ContentDetails
                        key={`${selectedPage.id}-${renderCycle}`}
                        page={selectedPage}
                        phase={tilePhase}
                        transitionToken={transitionToken}
                        onHideComplete={handleHideComplete}
                    />
                ) : null}
                {!selectedPage && !resumeMode && !linksMode ? contentViews : null}
            </div>
            <aside className={styles.menu} aria-label="Site navigation">
                <MenuHeader
                    name="Jason K. Smith"
                    subtitle="Design Engineer"
                    onClick={() => {
                        startFilterTransition(null);
                    }}
                />
                <div className={styles.links}>{desktopMenuLinks}</div>
            </aside>
            {mobileMenuOpen ? (
                <div
                    id={MOBILE_MENU_ID}
                    className={styles.mobileOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Site menu"
                >
                    <div className={styles.mobileOverlayHeader}>
                        <div className={styles.mobileOverlayBrand}>
                            <MenuHeader
                                name="Jason K. Smith"
                                subtitle="Design Engineer"
                                animated={false}
                                disableHover
                                onClick={() => handleMobileMenuNavigate(null)}
                            />
                        </div>
                        <button
                            type="button"
                            className={styles.mobileCloseButton}
                            aria-label="Close menu"
                            onClick={() => {
                                if (isMobile) {
                                    scheduleMobileMenuPaintOut(() => {
                                        setMobileMenuOpen(false);
                                        revealContentAfterMenuClose();
                                    }, 'uniformWipe');
                                } else {
                                    setMobileMenuOpen(false);
                                }
                            }}
                        >
                            <span
                                className={`${styles.mobileMenuIcon} ${styles.mobileMenuIconExpanded}`}
                                aria-hidden
                            >
                                <span className={styles.mobileMenuIconBar} />
                                <span className={styles.mobileMenuIconBar} />
                                <span className={styles.mobileMenuIconBar} />
                            </span>
                        </button>
                    </div>
                    <nav className={styles.mobileOverlayNav} aria-label="Primary">
                        <div className={styles.links}>
                            <MenuItem
                                index={0}
                                title="Selected Work"
                                isSelected={activeFilter === SitePageType.Project}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 0)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 0)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={() => handleMobileMenuNavigate(SitePageType.Project, { selectedRow: 0 })}
                            />
                            <MenuItem
                                index={1}
                                title="iOS Apps"
                                isSelected={activeFilter === SitePageType.iOSApp}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 1)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 1)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={() => handleMobileMenuNavigate(SitePageType.iOSApp, { selectedRow: 1 })}
                            />
                            <MenuItem
                                index={2}
                                title="Experiments"
                                isSelected={activeFilter === SitePageType.Experiment}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 2)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 2)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={() => handleMobileMenuNavigate(SitePageType.Experiment, { selectedRow: 2 })}
                            />
                            <MenuItem
                                index={3}
                                title="3D Modeling/Printing"
                                isSelected={activeFilter === SitePageType.ModelingPrinting}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 3)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 3)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={() =>
                                    handleMobileMenuNavigate(SitePageType.ModelingPrinting, { selectedRow: 3 })
                                }
                            />
                            <MenuItem
                                index={4}
                                title="Resume"
                                isSelected={resumeMode}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 4)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 4)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={handleMobileResumeNavigate}
                            />
                            <MenuItem
                                index={5}
                                title="Links"
                                isSelected={linksMode}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 5)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 5)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={handleMobileLinksNavigate}
                            />
                            <MenuItem
                                index={6}
                                title="Contact"
                                isSelected={false}
                                disableHover
                                paintOutSignal={menuPaintOutTick}
                                paintOutGeneration={menuPaintOutGen}
                                paintOutVariant={mobileMenuPaintOutVariantForRow(mobileMenuPaintOutMode, 6)}
                                paintOutExitDelayMs={mobileOverlayPaintOutExitDelayMs(mobileMenuPaintOutMode, 6)}
                                onPaintOutComplete={onMobileMenuPaintOutPartDone}
                                onClick={handleMobileContactNavigate}
                            />
                        </div>
                    </nav>
                </div>
            ) : null}
        </div>
        </>
    );
};

export default ContentFlow;
